import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

export default function SitesSection() {
  const { lang } = useLang();
  const en = lang === "en";
  const [sites, setSites] = useState([]);
  useEffect(() => {
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
  }, []);

  return (
    <section className="relative py-32 md:py-40 px-6 md:px-12" data-testid="sites-section">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-16 md:mb-24">
          <div>
            <div className="meta-label text-cinabre">§ {en ? "The 6 sites" : "Les 6 sites"} · Canal'Art 2026</div>
            <h2 className="font-serif font-light text-5xl md:text-7xl leading-[0.95] tracking-tight mt-6 max-w-3xl">
              {en ? <>Six stops, <em className="italic">thirty artists</em> per site.</> : <>Six escales, <em className="italic">trente artistes</em> par site.</>}
            </h2>
            <div className="mt-6 max-w-xl">
              <div className="inline-flex items-baseline flex-wrap gap-x-3 gap-y-1 font-serif text-2xl md:text-3xl leading-tight">
                <span className="text-cinabre">{en ? "30 years UNESCO" : "30 ans UNESCO"}</span>
                <span className="text-fg/40">=</span>
                <span>{en ? "30 artists per site." : "30 artistes par site."}</span>
              </div>
              <div className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">
                {en ? "1996 → 2026 · one artist for each of the thirty years of inscription · " : "1996 → 2026 · un artiste pour chacune des trente années d'inscription · "}<span className="text-cinabre">#30CMDUNESCO</span>
              </div>
            </div>
          </div>
          <Link to="/sites" className="btn-editorial ghost" data-testid="see-all-sites">
            {en ? "Explore the 6 sites" : "Explorer les 6 sites"} <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {sites.map((s, i) => {
            const wide = i % 3 === 0;
            return (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: (i % 3) * 0.05 }}
                className={`${wide ? "md:col-span-8" : "md:col-span-4"}`}
                data-testid={`site-card-${s.slug}`}
              >
                <Link to={`/sites/${s.slug}`} className="group relative overflow-hidden border border-fg/15 block">
                <div className="relative overflow-hidden h-[380px] md:h-[460px]">
                  <img
                    src={s.image}
                    alt={s.name}
                    className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-fg/25 group-hover:bg-fg/45 transition-colors duration-700" />
                  <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-between text-bg">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] tracking-[0.24em] uppercase">{en ? "Chapter" : "Chapitre"} {s.chapter}</span>
                      <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-bg/80">{s.km}</span>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-bg/80 mb-3">{s.role}</div>
                      <h3 className="font-serif text-4xl md:text-5xl leading-[1] tracking-tight">{s.name}</h3>
                    </div>
                  </div>
                </div>
                <div className="p-6 md:p-8 bg-bg flex items-start justify-between gap-4">
                  <p className="text-fg/75 leading-relaxed flex-1">{s.description}</p>
                  <span className="shrink-0 font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre group-hover:translate-x-1 transition-transform">{en ? "View →" : "Voir →"}</span>
                </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
